import { useState } from "react";
import { Trash2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, money } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function warnings(r, chk) {
  const w = [];
  (chk?.orders || []).forEach((o) => w.push(`Linked order ${o.order_number} (${o.status}${o.payment_status === "paid" ? `, paid ${money(o.total)}` : ""}) is kept as is. No Stripe refund or cancellation happens.`));
  if (r.pending_session_id && !(chk?.orders || []).length) w.push("Has an open Stripe checkout. It isn't cancelled.");
  if (chk?.active_hold_in) w.push(`${chk.active_hold_in}" of fabric is on hold. It is NOT returned now; it stays held until the hold expires.`);
  if (chk?.committed_in) w.push(`${chk.committed_in}" of fabric was already deducted at approval. It is NOT restored.`);
  if (chk?.messages) w.push(`${chk.messages} message${chk.messages === 1 ? "" : "s"} will no longer be visible from this request.`);
  return w;
}

// Row-level (outside the request window) so the confirmation always sits on top.
export function DeleteRequestButton({ request, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [chk, setChk] = useState(null);
  const [busy, setBusy] = useState(false);

  const openDialog = async () => {
    setChk(null); setOpen(true);
    try { setChk((await api.get(`/admin/custom-requests/${request.id}/delete-check`)).data); }
    catch (e) { setChk({}); toast.error(apiError(e)); }
  };

  const del = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.delete(`/admin/custom-requests/${request.id}`);
      toast.success(`${request.request_number} permanently deleted`);
      setOpen(false); onDeleted();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const w = warnings(request, chk);
  return (
    <>
      <button data-testid={`delete-request-${request.request_number}`} aria-label="Delete request" onClick={openDialog}
        className="flex h-10 w-10 items-center justify-center rounded-full text-kys-charcoal transition-colors hover:bg-red-50 hover:text-destructive">
        <Trash2 className="h-5 w-5" />
      </button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="delete-request-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete this request?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-1 text-sm">
                <p data-testid="delete-request-summary"><span className="font-mono">{request.request_number}</span> · {request.customer?.name} · {request.customer?.email} · {request.status}</p>
                <p className="font-semibold text-destructive">This is permanent and can't be undone. Only this request record is deleted. No emails are sent, and stock, fabric, orders, Stripe and Shippo are not changed.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          {chk === null ? <div className="flex items-center gap-2 text-xs text-kys-muted"><Loader2 className="h-4 w-4 animate-spin" /> Checking linked records…</div> : w.length > 0 && (
            <ul data-testid="delete-request-warnings" className="space-y-2 rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              {w.map((t) => <li key={t} className="flex gap-2"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{t}</li>)}
            </ul>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-request-cancel">Keep request</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-request-confirm" disabled={busy || chk === null} onClick={del} className="bg-destructive text-white hover:bg-destructive/90">
              {busy ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
